import {
    AfterViewInit, Directive, EventEmitter, Inject,
    Input, OnDestroy, Output,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Observable ,  ReplaySubject ,  Subscription } from "rxjs";
import { GenericActions } from "../../redux/generic-actions";
import { ImmutableRedux } from "../../redux/immutable-redux.service";
import { ReduxRequestsMap } from "../../redux/redux-requests-map.service";
import { GenericFormActions } from "./actions";
import { ISaveAttempt } from "./common-form.interface";
import { ICommonFormState } from "./common-form.interface";
import { defaultInitialState } from "./reducer";

import { get as _get, isEmpty } from "lodash";
import * as traverse from "traverse";
import { FORM_CTAS_STATE_PROP, FORM_STATE_PROP } from "./state-props";

@Directive({
    selector: "[common-form-manager]",
    exportAs: "CommonFormManager",
})
export class CommonFormManagerDirective implements OnDestroy, AfterViewInit {

    @Input() public stateProp: string;
    @Input() public shouldFetchDetails = true;
    @Input() public set state(value: ICommonFormState) {
        this._state = this._state.merge(value);
    }

    public get state() {
        return this._state;
    }

    private _state = defaultInitialState;
    private _skipReset = false;

    @Output() formDataReception: EventEmitter<any> = new EventEmitter();
    @Output() formDataInitialization: EventEmitter<any> = new EventEmitter();
    @Output() formSaveAttempt: EventEmitter<ISaveAttempt> = new EventEmitter();
    @Output() formClose$: EventEmitter<any> = new EventEmitter();

    public formState: any;
    public fields = {};
    public fieldHistory = {};
    public fieldGroupLists = {};
    public isFormVisible = false;
    public errorMessage = "";
    public isValid = true;
    public formDataInitialized = false;
    public formIsBeingInitialized = true;
    public detailFetched = false;

    public state$: Subscription;
    public saveAttemptSubscription: Subscription;
    public stateObservable$: Observable<any>;
    public stateReplaySubject$ = new ReplaySubject(1);

    constructor(
        public route: ActivatedRoute,
        public ngRedux: ImmutableRedux<any>,
        public reduxRequests: ReduxRequestsMap,
        @Inject(FORM_STATE_PROP) public rootProp,
        @Inject(FORM_CTAS_STATE_PROP) public formCtasRootProp,
    ) { }

    public ngAfterViewInit() {
        this.stateObservable$ = this.ngRedux.select(this.rootProp, this.stateProp);
        this.state$ = this.stateObservable$.subscribe((state) => {
            this.stateReplaySubject$.next(state);
            this.formState = state;
            if (!!state.formValidation && !!state.formUI) {
                this.isFormVisible = state.formUI.isVisible;
                this.errorMessage = state.formValidation.mutationMessage.message;
            }
            if (this.formState && this.formState.formUI.isVisible) {
                if (!this.shouldFetchDetails) { return; }

                if (this.detailId && this.detailFetched === false) {
                    this.getDetail(this.detailId);
                    this.detailFetched = true;
                }

                this.formIsBeingInitialized = false;
            }
        });

        this.saveAttemptSubscription = this.formSaveAttempt.subscribe((e) => {
            if (e.success && e.closeOnSave) {
                this.closeForm();
            }
        });
    }

    public ngOnDestroy() {
        this.state$.unsubscribe();
        this.saveAttemptSubscription.unsubscribe();
        this.resetState();
        Object.keys(this.fieldHistory).forEach((prop) => {
            this.fieldHistory[prop].resetState();
        });
    }

    public resetState(options?) {
        this.ngRedux.dispatchFactory(GenericActions.STATE_RESET, this.rootProp, this.stateProp, this.state, options);
        this.detailFetched = false;
    }

    public resetFieldState(fieldProp) {
        this.dispatch(GenericFormActions.FIELD_STATE_RESET, this.rootProp, fieldProp);
    }

    public registerField(field) {
        this.fields[field.context.stateProp] = field;
        this.fieldHistory[field.context.stateProp] = field;
    }

    public unregisterField(field) {
        delete this.fields[field.context.stateProp];
    }

    public updateForm(data: any) {
        Object.keys(this.fields).forEach((fieldKey) => {
            const field = this.fields[fieldKey];
            const fieldValue = _get(data, field.context.fieldProp.split("::"));
            field.loadData(fieldValue);
        });
    }

    public getDetail(id: string) {
        this.reduxRequests.within(this.stateProp).getDetail({ id })
            .map((body) => body.data)
            .subscribe((payload) => {
                this.formDataReception.emit(payload);
            });
    }

    public onFormFieldChange(fieldProp, payload: any, options?) {
        this.dispatch(GenericFormActions.FIELD_VALUE_UPDATED, this.rootProp, fieldProp, payload, options);
    }

    public load(payload, partial?: boolean, fieldProp?: string, partialInsertIndex?: number) {
        if (isEmpty(payload)) {
            return;
        }
        const options = { partial, fieldProp, partialInsertIndex };
        this.formDataInitialized = true;

        if (!partial) { this.updateForm(payload); }

        this.ngRedux.dispatchFactory(
            GenericActions.DETAIL_LOADED, this.rootProp, this.stateProp, payload, { options });

        if (!!partial) { this.updateForm(this.formState.formData); }

        this.formDataInitialization.emit({ success: true, formData: payload });
    }

    public loadGroups(data: any) {

        if (isEmpty(data)) {
            return;
        }

        const paths = traverse(data).paths();
        paths.forEach((fieldGroupListKeyPath: any, i) => {
            const concatenatedFieldGroupListKeyPath = fieldGroupListKeyPath.join("::");
            const groupList = this.fieldGroupLists[concatenatedFieldGroupListKeyPath];
            if (!!groupList && (concatenatedFieldGroupListKeyPath === groupList.fieldProp)) {
                const existingData = _get(data, groupList.fieldProp.split("::"));
                if (!!existingData) {
                    groupList.loadGroups(existingData);
                }
            }
        });

    }

    public loadDropdownLabels(data: any) {
        if (isEmpty(data)) {
            return;
        }

        Object.keys(this.fields).forEach((key) => {
            const field = this.fields[key];
            const label = _get(data, field.fieldProp);
            if (label !== undefined) {
                const newState = field.fieldStateService.newState({ selectedLabel: label });

                if (key === "featureProductVersions::licensedServiceId" && label) {
                    newState.selectedLabel = label.customerName;
                    newState.selectedValue = label.id;
                }
                field.next(newState);
            }
        });
    }

    public dispatch(actionType: string, fieldRootProp: string, fieldProp: string, payload?: any, options?) {
        this.ngRedux.dispatchFactory(actionType, fieldRootProp, this.stateProp, payload, { fieldProp, options });
    }

    public registerFieldGroupList(fieldGroupList) {
        this.fieldGroupLists[fieldGroupList.fieldProp] = fieldGroupList;
    }

    public unregisterFieldGroupList(fieldGroupList) {
        delete this.fieldGroupLists[fieldGroupList.fieldProp];
    }

    public addNewBlankGroup(groupRootProp, fieldProp: string, groupProp: string, groupContext?: any) {
        this.dispatch(GenericFormActions.BLANK_GROUP_ADDED, this.rootProp, fieldProp, undefined, { groupContext });
        this.ngRedux.dispatchFactory(GenericFormActions.BLANK_GROUP_ADDED, groupRootProp, groupProp, { groupContext });
    }

    public removeGroup(index, groupRootProp, fieldProp: string, groupProp: string) {
        this.dispatch(GenericFormActions.GROUP_REMOVED, this.rootProp, fieldProp, index);
        this.ngRedux.dispatchFactory(GenericFormActions.GROUP_REMOVED, groupRootProp, groupProp, index);
    }

    public get validityCheckPasses() {
        this.isValid = true;
        Object.keys(this.fields).forEach((fieldKey) => {
            const field = this.fields[fieldKey];
            field.touch();
            if (field.isInvalid) {
                this.isValid = false;
            }
        });
        return this.isValid;
    }

    public saveForm(closeOnSave: boolean, skipReset: boolean) {
        if (this.validityCheckPasses) {
            this._skipReset = skipReset;
            const formData = { ...this.formState.formData };
            this.reduxRequests.within(this.stateProp).mutate(formData, this.detailId).subscribe(
                (res) => this.dispatchServerSuccess(res, closeOnSave),
                (error) => {
                    if (error.message && error.message.includes("GraphQL error: 401 -", " ")) {
                        const parsed = JSON.parse(error.message.replace("GraphQL error: 401 -", " ").trim());
                        if (parsed.error && parsed.error.reason === "PermissionDeniedException") {
                            error.message = "You don't have access to perform this action";
                        }
                    }
                    this.dispatchServerError(error);
                },
            );
        } else {
            this.formSaveAttempt.emit({ success: false, isServerError: false, closeOnSave: false });
        }
    }

    public dispatchServerError(error) {
        this.ngRedux.dispatchFactory(GenericFormActions.SERVER_ERROR_MESSAGE_ADDED, this.rootProp, this.stateProp, error);
        this.formSaveAttempt.emit({ success: false, message: (error || error.message), isServerError: true, closeOnSave: false });
    }

    public dispatchServerSuccess(res, closeOnSave: boolean) {
        this.ngRedux.dispatchFactory(GenericFormActions.SAVE_SUCCESS, this.rootProp, this.stateProp, "Success!");
        this.formSaveAttempt.emit({ success: true, closeOnSave });
    }

    public dispatchClientError(fieldProp, fieldLabel, errorMessage) {
        const payload = { fieldLabel, errorMessage };
        this.dispatch(GenericFormActions.CLIENT_ERROR_MESSAGE_ADDED, this.rootProp, fieldProp, payload);
    }

    public clearFieldData(fieldProp: string) {
        this.onFormFieldChange(fieldProp, undefined);
    }

    public removeFieldFromForm(fieldProp: string) {
        this.dispatch(GenericFormActions.FIELD_REMOVED_FROM_FORM, this.rootProp, fieldProp);
    }

    public closeForm() {
        this.ngRedux.dispatchFactory(GenericFormActions.FORM_CLOSED, this.rootProp, this.stateProp);
        this.ngRedux.dispatchFactory(GenericFormActions.FORM_CLOSED, this.formCtasRootProp, this.stateProp);

        if (!this._skipReset) { this.resetState(); }
        this.formClose$.emit(true);
    }

    public get detailIdFromState() {
        return !!this.formState ? this.formState.formReadOnly.detailId : undefined;
    }

    public get detailIdFromRoute() {
        const params = this.route.snapshot.params;
        return !!params ? params.detailToEditId : undefined;
    }

    public get detailId() {
        return this.detailIdFromState || this.detailIdFromRoute;
    }

    public get isEditForm(): boolean {
        return !!this.detailId;
    }

    public get disableFieldsOnOpen(): boolean {
        return this.isEditForm || this.formIsBeingInitialized;
    }

    public get isDuplicated(): boolean {
        return this.route.snapshot.url.join("/").search("duplicated") !== -1;
    }
}
